import {
  BookingStatusLabels,
  StorageObjectGroupRecordForTransferOrder,
  StorageObjectGroupRecordList,
  StorageObjectGroupRecordListV2,
  StorageObjectGroupRecordListWithBookings,
  StorageTransferOrderItemUnableToCompleteReasonLabels,
  WheelExternalCheckStatus
} from '@navio/nsdk';
import { NoddiBadge, NoddiColumnDef } from 'noddi-ui-web';
import {
  getBookingTimeWindowDisplayText,
  getCarDisplayName,
  getStorageGroupLocationDisplay,
  getStorageGroupStatus,
  NoddiDateFormats,
  noddiFormatDate
} from 'noddi-util';
import { CarInfoCell } from '../CarDetails/Cards/CarInfoCell';
import { LatestCompletedBookingCell } from '../FleetView/LatestCompletedBookingCell';
import { UpcomingBookingCell } from '../FleetView/UpcomingBookingCell';
import { customerInfoName, customerInfoSearchLabel } from '../UserGroups/customerInfo';
import { StorageLocationInfo } from './StorageLocationInfo';
import { TransferOrdersBadges } from './TransferOrdersBadges';
import { WheelSetBookingCars } from './WheelSetBookingCars';

// All wheel-set row variants share metadata/carWheelSet/owner/state/latestEvent.
type AnyWheelSetRow =
  | StorageObjectGroupRecordList
  | StorageObjectGroupRecordListWithBookings
  | StorageObjectGroupRecordForTransferOrder;

// Inventory list rows (v2 endpoint) — carry both
// `upcomingTransferOrders` and per-car booking fields.
type WheelSetRowWithUpcomingTransferOrders = StorageObjectGroupRecordListV2;

// Rows that carry an upcoming-bookings list with delivery window,
// status, and the booking items needed for the per-booking columns.
type WheelSetRowWithBookings = StorageObjectGroupRecordListWithBookings | StorageObjectGroupRecordForTransferOrder;

// Cell renderers are top-level named functions typed against the
// narrowest row union each one needs. They're referenced from inline
// column literals below — TanStack's ColumnDef<TData> is invariant in
// TData (header/footer template positions), so we can't share whole
// column constants across arrays with different TData parameters, but
// cell functions are contravariant in their input and compose fine.

const renderWheelSetCell = ({ row }: { row: { original: AnyWheelSetRow } }) => {
  const { carWheelSet, owner, metadata } = row.original;
  const ownerName = owner ? customerInfoName(owner) : null;

  return (
    <CarInfoCell
      car={carWheelSet ?? {}}
      season={metadata.season ?? null}
      leading={carWheelSet ? undefined : <span className='text-sm font-medium'>{metadata.name}</span>}
    >
      {ownerName ? <span className='truncate'>{ownerName}</span> : null}
    </CarInfoCell>
  );
};

const renderStorageLocationCell = ({ row }: { row: { original: AnyWheelSetRow } }) => {
  const state = row.original.state;
  const latestEvent = row.original.latestEvent;

  return <StorageLocationInfo state={state} latestEvent={latestEvent} />;
};

const renderTransferOrdersCell = ({ row }: { row: { original: WheelSetRowWithUpcomingTransferOrders } }) => (
  <TransferOrdersBadges transferOrders={row.original.upcomingTransferOrders} />
);

const externalCheckBadgeVariant: Record<WheelExternalCheckStatus, 'outline' | 'success' | 'destructive'> = {
  PENDING: 'outline',
  READY: 'success',
  ISSUE: 'destructive'
};

const externalCheckLabel: Record<WheelExternalCheckStatus, string> = {
  PENDING: 'Awaiting check',
  READY: 'Ready',
  ISSUE: 'Issue reported'
};

const renderUrgencyCell = ({ row }: { row: { original: StorageObjectGroupRecordForTransferOrder } }) => {
  const urgency = row.original.urgency;
  if (!urgency) {
    return null;
  }
  return (
    <div className='flex max-w-60 flex-col gap-1'>
      <NoddiBadge variant='warning'>Urgent</NoddiBadge>
      {urgency.comment && <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{urgency.comment}</p>}
      <p className='text-xs text-muted-foreground'>{noddiFormatDate(urgency.markedAt, NoddiDateFormats.DATE_TIME)}</p>
      {urgency.markedBy && (
        <p className='text-xs text-muted-foreground'>
          {urgency.markedBy.firstName} {urgency.markedBy.lastName}
        </p>
      )}
    </div>
  );
};

const renderUnableToCompleteCell = ({ row }: { row: { original: StorageObjectGroupRecordForTransferOrder } }) => {
  const unableToComplete = row.original.unableToComplete;
  if (!unableToComplete) {
    return null;
  }
  return (
    <div className='flex max-w-60 flex-col gap-1'>
      <NoddiBadge variant='destructive'>
        {StorageTransferOrderItemUnableToCompleteReasonLabels[unableToComplete.reason]}
      </NoddiBadge>
      {unableToComplete.comment && (
        <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{unableToComplete.comment}</p>
      )}
      {unableToComplete.markedAt && (
        <p className='text-xs text-muted-foreground'>
          {noddiFormatDate(unableToComplete.markedAt, NoddiDateFormats.DATE_TIME)}
        </p>
      )}
    </div>
  );
};

const renderExternalCheckCell = ({ row }: { row: { original: StorageObjectGroupRecordForTransferOrder } }) => {
  const externalCheck = row.original.externalCheck;
  if (!externalCheck) {
    return null;
  }
  return (
    <div className='flex max-w-60 flex-col gap-1'>
      <NoddiBadge variant={externalCheckBadgeVariant[externalCheck.status]}>
        {externalCheckLabel[externalCheck.status]}
      </NoddiBadge>
      {externalCheck.status === 'ISSUE' && externalCheck.comment && (
        <p className='text-xs whitespace-pre-wrap text-muted-foreground'>{externalCheck.comment}</p>
      )}
      {externalCheck.checkedAt && (
        <p className='text-xs text-muted-foreground'>
          {noddiFormatDate(externalCheck.checkedAt, NoddiDateFormats.DATE_TIME)}
        </p>
      )}
    </div>
  );
};

const renderUpcomingBookingDetailsCell = ({ row }: { row: { original: WheelSetRowWithBookings } }) => {
  const booking = row.original.bookings?.[0];
  if (!booking) {
    return null;
  }

  const carId = row.original.carWheelSet?.carId;

  return (
    <div className='flex max-w-52 flex-col gap-2'>
      {carId != null && <WheelSetBookingCars bookingCars={booking.bookingItemsCar ?? []} carId={carId} />}
      <div className='flex flex-col gap-1'>
        <NoddiBadge>{BookingStatusLabels[booking.status.value]}</NoddiBadge>
        <span className='text-sm whitespace-nowrap'>
          {getBookingTimeWindowDisplayText({
            timeWindow: {
              startsAt: booking.deliveryWindowStartsAt,
              endsAt: booking.deliveryWindowEndsAt,
              timeZone: booking.address.timezone
            }
          })}
        </span>
      </div>
    </div>
  );
};

const wheelSetColumnDef = {
  header: 'Wheel set',
  accessorFn: (row: AnyWheelSetRow) =>
    [
      row.owner ? customerInfoSearchLabel(row.owner) : null,
      row.carWheelSet
        ? getCarDisplayName({ car: row.carWheelSet, showLicensePlate: true, showYear: false })
        : row.metadata.name
    ]
      .filter(Boolean)
      .join(' '),
  cell: renderWheelSetCell
} as const;

const storageLocationColumnDef = {
  header: 'Location',
  id: 'storage',
  meta: {
    description: 'Where the wheel set is: a storage unit, a vehicle, in transit or with the customer.'
  },
  cell: renderStorageLocationCell
} as const;

const transferOrdersColumnDef = {
  header: 'Transfer order',
  id: 'transferOrder',
  meta: {
    description: 'Upcoming transfer order (draft, created, or in progress) for this wheel set.'
  },
  cell: renderTransferOrdersCell
} as const;

const upcomingBookingDetailsColumnDef = {
  header: 'Upcoming booking',
  id: 'upcomingBookingDetails',
  accessorKey: 'bookings',
  meta: {
    description: 'Booked cars and services, booking status, and delivery time window.'
  },
  cell: renderUpcomingBookingDetailsCell
} as const;

const renderLatestCompletedBookingCell = ({ row }: { row: { original: WheelSetRowWithUpcomingTransferOrders } }) => (
  <LatestCompletedBookingCell booking={row.original.latestCompletedBooking} />
);

const renderUpcomingBookingCell = ({ row }: { row: { original: WheelSetRowWithUpcomingTransferOrders } }) => (
  <UpcomingBookingCell booking={row.original.upcomingBooking} />
);

const latestCompletedBookingColumnDef = {
  header: 'Last booking',
  id: 'latestCompletedBooking',
  cell: renderLatestCompletedBookingCell
} as const;

const upcomingBookingColumnDef = {
  header: 'Upcoming booking',
  id: 'upcomingBooking',
  cell: renderUpcomingBookingCell
} as const;

export const wheelSetColumns: NoddiColumnDef<WheelSetRowWithUpcomingTransferOrders>[] = [
  wheelSetColumnDef,
  storageLocationColumnDef,
  transferOrdersColumnDef,
  latestCompletedBookingColumnDef,
  upcomingBookingColumnDef
];

export const wheelSetWithBookingsColumns: NoddiColumnDef<StorageObjectGroupRecordListWithBookings>[] = [
  wheelSetColumnDef,
  upcomingBookingDetailsColumnDef,
  storageLocationColumnDef,
  transferOrdersColumnDef
];

const urgencyColumnDef = {
  header: 'Urgency',
  id: 'urgency',
  meta: {
    description:
      'Dispatch urgency flag set by ops. Urgent wheel sets are sorted before non-urgent ones within the same delivery date.'
  },
  cell: renderUrgencyCell
} as const;

const externalCheckColumnDef = {
  header: 'Public check',
  id: 'externalCheck',
  meta: {
    description: 'Source-facility recipient verdict captured via the public ready/issue link on this transfer order.'
  },
  cell: renderExternalCheckCell
} as const;

const unableToCompleteColumnDef = {
  header: 'Unable to complete',
  id: 'unableToComplete',
  meta: {
    description:
      'Worker flag indicating the wheel set could not be moved on this order. While flagged, the wheel set can join another transfer order.'
  },
  cell: renderUnableToCompleteCell
} as const;

// Same shape as wheelSetWithBookingsColumns but typed for the slim
// transfer-order detail record and without the upcomingTransferOrders
// badge column (the current order would always show up there, so it
// adds noise without information on the detail page). Adds the
// externalCheck column so staff can see the source-facility recipient's
// per-wheel-set verdict alongside everything else.
export const wheelSetForTransferOrderColumns: NoddiColumnDef<StorageObjectGroupRecordForTransferOrder>[] = [
  { ...wheelSetColumnDef, id: 'wheelSet', meta: { orderingKey: 'wheel-set' } },
  {
    ...urgencyColumnDef,
    accessorFn: (row) => (row.urgency ? 1 : 0),
    meta: { ...urgencyColumnDef.meta, orderingKey: 'urgency' }
  },
  {
    ...externalCheckColumnDef,
    accessorFn: (row) => (row.externalCheck ? externalCheckLabel[row.externalCheck.status] : undefined),
    sortUndefined: 'last',
    meta: { ...externalCheckColumnDef.meta, orderingKey: 'external-check' }
  },
  {
    ...unableToCompleteColumnDef,
    accessorFn: (row) =>
      row.unableToComplete
        ? StorageTransferOrderItemUnableToCompleteReasonLabels[row.unableToComplete.reason]
        : undefined,
    sortUndefined: 'last',
    meta: { ...unableToCompleteColumnDef.meta, orderingKey: 'unable-to-complete' }
  },
  {
    ...upcomingBookingDetailsColumnDef,
    accessorFn: (row) => row.bookings?.[0]?.deliveryWindowStartsAt ?? undefined,
    sortUndefined: 'last',
    meta: { ...upcomingBookingDetailsColumnDef.meta, orderingKey: 'upcoming-booking' }
  },
  {
    ...storageLocationColumnDef,
    accessorFn: (row) => {
      const location = getStorageGroupLocationDisplay(row.state);
      return location
        ? [location.facilityName, location.unitLine, location.originFacilityName, location.originUnitLine]
            .filter(Boolean)
            .join(' ')
        : getStorageGroupStatus(row.state).label;
    },
    meta: { ...storageLocationColumnDef.meta, orderingKey: 'storage' }
  }
];
